import React from 'react';

/**
 * Returns true if the output value should be updated.
 */
export interface ShouldUpdateValue<T> {
  (currentValue: T, previousValue: T): boolean;
}

/**
 * Returns the value provided during the previous render, and returns
 * 'currentValue' during the first render.
 * @param currentValue The value to compare.
 * @param shouldUpdateValue Optional predicate that controls when update the
 * previous value, defaults to `() => true`.
 *
 * @example
 * ```typescript
 *   // The value provided to the hook during the previous render.
 *   const alwaysGetPreviuosValue = usePreviousValue(currentVal);
 *
 *   // The latest previous truthy value (ignores non truthy values).
 *   const previousTruthy = usePreviousValue(currentVal, Boolean);
 * ```
 * @see https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
 */
const usePreviousValue = <T = any>(
  currentValue: T,
  shouldUpdateValue: ShouldUpdateValue<T> = () => true,
) => {
  const previousValueRef = React.useRef<T>(currentValue);

  React.useEffect(() => {
    const { current: previousValue } = previousValueRef;

    if (shouldUpdateValue(currentValue, previousValue)) {
      previousValueRef.current = currentValue;
    }
  }, [currentValue, shouldUpdateValue]);

  return previousValueRef.current;
};

export default usePreviousValue;
